// =============================================================================
// QuotePage — accept a team outing quote (work item team-uitjes)
// =============================================================================
// manage.neoseum.com/offerte?q=<token>[&lang=en], the link in the quote and
// reminder mails (Rick's spec "Teamuitje: reserveren via offerte",
// 2026-10-21): shows the quote with an Accept button and a Decline button
// (Dirk, 2026-10-23: frees the time slots at once), or says it has expired,
// was declined, withdrawn or already accepted. Talks to the booking-team edge
// function; the token is the only key.
import { useEffect, useState } from 'react';

const API = import.meta.env.VITE_BOOKING_API_URL as string;
const ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

type Status = 'requested' | 'accepted' | 'invoiced' | 'paid' | 'expired' | 'cancelled';

interface Quote {
  quote_number: string;
  status: Status;
  locale: 'nl' | 'en';
  company: string;
  contact_name: string;
  email: string;
  invoice_email: string | null;
  location: { name: string; address_line: string | null; postal_code: string | null; city: string | null };
  start_at: string;
  end_at: string;
  parts: Array<{ slot_start: string; people: number }>;
  group_size: number;
  slot_count: number;
  places_per_slot: number;
  price_per_slot_ex_cents: number;
  total_ex_cents: number;
  vat_cents: number;
  total_inc_cents: number;
  vat_rate_pct: number;
  expires_at: string;
  declined: boolean | null;
}

const TZ = 'Europe/Amsterdam';

const T = {
  nl: {
    head: 'Offerte teamuitje',
    loading: 'Laden…',
    notFound: 'Deze offerte bestaat niet. Gebruik de link uit je e-mail, of mail bookings@neoseum.com.',
    error: 'Er ging iets mis. Probeer het opnieuw of mail bookings@neoseum.com.',
    quoteNo: 'Offertenummer',
    company: 'Bedrijf',
    location: 'Locatie',
    date: 'Datum',
    time: 'Tijd',
    people: 'Personen',
    slots: 'Tijdsloten',
    slotsValue: (n: number, p: number) => `${n} × ${p} plekken`,
    schedule: 'Schema',
    group: (k: number) => `groep ${k}`,
    exVat: 'Prijs excl. btw',
    vat: (r: number) => `Btw ${r}%`,
    incVat: 'Totaal incl. btw',
    validUntil: (d: string) => `Deze offerte is geldig tot ${d}. Tot dan houden we de tijdsloten voor je vast.`,
    terms: 'Na acceptatie is de reservering definitief. Geboekte tijdsloten worden niet terugbetaald, ook niet als er minder mensen komen. Je ontvangt de factuur per mail; betaal deze uiterlijk 3 dagen voor aanvang, of binnen 5 werkdagen na de factuurdatum als dat eerder is.',
    agree: 'Ik ga akkoord met de voorwaarden hierboven',
    accept: 'Offerte accepteren',
    accepting: 'Bezig…',
    acceptedTitle: 'Je teamuitje is bevestigd',
    acceptedBody: (e: string) => `Bedankt! De reservering is definitief. We hebben een bevestiging gestuurd naar ${e}; de factuur volgt per mail.`,
    alreadyTitle: 'Deze offerte is al geaccepteerd',
    alreadyBody: 'De reservering is definitief. De factuur volgt per mail (of is al verstuurd).',
    expiredTitle: 'Deze offerte is verlopen',
    expiredBody: 'De offerte is niet binnen 48 uur geaccepteerd en de tijdsloten zijn vrijgegeven. Vraag gerust een nieuwe offerte aan.',
    cancelledTitle: 'Deze offerte is ingetrokken',
    cancelledBody: 'Neem contact met ons op als je vragen hebt: bookings@neoseum.com of 085 060 9788.',
    decline: 'Offerte afwijzen',
    declineAsk: 'Wil je de offerte afwijzen? We geven de tijdsloten direct vrij. Dit kun je niet ongedaan maken.',
    declineYes: 'Ja, afwijzen',
    declineNo: 'Nee, terug',
    declining: 'Bezig…',
    declinedTitle: 'Je hebt de offerte afgewezen',
    declinedBody: 'De tijdsloten zijn vrijgegeven. Wil je toch een teamuitje plannen? Vraag gerust een nieuwe offerte aan.',
    newQuote: 'Nieuwe offerte aanvragen',
    teamsUrl: 'https://neoseum.com/nl/teams',
    arrive: 'Kom 5 minuten voor je eerste tijdslot.',
    questions: 'Vragen?',
  },
  en: {
    head: 'Team outing quote',
    loading: 'Loading…',
    notFound: 'This quote does not exist. Use the link in your email, or email bookings@neoseum.com.',
    error: 'Something went wrong. Please try again or email bookings@neoseum.com.',
    quoteNo: 'Quote number',
    company: 'Company',
    location: 'Location',
    date: 'Date',
    time: 'Time',
    people: 'People',
    slots: 'Time slots',
    slotsValue: (n: number, p: number) => `${n} × ${p} places`,
    schedule: 'Schedule',
    group: (k: number) => `group ${k}`,
    exVat: 'Price excl. VAT',
    vat: (r: number) => `VAT ${r}%`,
    incVat: 'Total incl. VAT',
    validUntil: (d: string) => `This quote is valid until ${d}. We hold the time slots for you until then.`,
    terms: 'Once you accept, the reservation is final. Booked time slots are not refunded, also when fewer people come. You receive the invoice by email; please pay it no later than 3 days before your visit, or within 5 working days of the invoice date if that is sooner.',
    agree: 'I agree to the terms above',
    accept: 'Accept quote',
    accepting: 'Working…',
    acceptedTitle: 'Your team outing is confirmed',
    acceptedBody: (e: string) => `Thank you! The reservation is final. We have sent a confirmation to ${e}; the invoice follows by email.`,
    alreadyTitle: 'This quote has already been accepted',
    alreadyBody: 'The reservation is final. The invoice follows by email (or has been sent already).',
    expiredTitle: 'This quote has expired',
    expiredBody: 'The quote was not accepted within 48 hours and the time slots have been released. Feel free to request a new quote.',
    cancelledTitle: 'This quote has been withdrawn',
    cancelledBody: 'Please get in touch if you have questions: bookings@neoseum.com or +31 85 060 9788.',
    decline: 'Decline quote',
    declineAsk: 'Do you want to decline this quote? We release the time slots straight away. This cannot be undone.',
    declineYes: 'Yes, decline',
    declineNo: 'No, go back',
    declining: 'Working…',
    declinedTitle: 'You have declined the quote',
    declinedBody: 'The time slots have been released. Still want to plan a team outing? Feel free to request a new quote.',
    newQuote: 'Request a new quote',
    teamsUrl: 'https://neoseum.com/teams',
    arrive: 'Please arrive 5 minutes before your first time slot.',
    questions: 'Questions?',
  },
} as const;

function money(cents: number, l: 'nl' | 'en') {
  return new Intl.NumberFormat(l === 'nl' ? 'nl-NL' : 'en-GB', { style: 'currency', currency: 'EUR' })
    .format(cents / 100);
}
function day(iso: string, l: 'nl' | 'en') {
  return new Intl.DateTimeFormat(l === 'nl' ? 'nl-NL' : 'en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: TZ,
  }).format(new Date(iso));
}
function time(iso: string) {
  return new Intl.DateTimeFormat('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: TZ })
    .format(new Date(iso));
}

async function call(body: Record<string, unknown>): Promise<{ status: number; json: any }> {
  const res = await fetch(`${API}/booking-team`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: ANON, Authorization: `Bearer ${ANON}` },
    body: JSON.stringify(body),
  });
  let json: any = null;
  try { json = await res.json(); } catch { /* ignore */ }
  return { status: res.status, json };
}

export function isQuoteRoute(): boolean {
  const params = new URLSearchParams(window.location.search);
  return window.location.pathname.replace(/\/+$/, '') === '/offerte' || params.has('q');
}

export function QuotePage() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('q') ?? '';
  const [lang, setLang] = useState<'nl' | 'en'>(params.get('lang') === 'en' ? 'en' : 'nl');
  const [quote, setQuote] = useState<Quote | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'not_found' | 'error'>('loading');
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState(false);
  const [justAccepted, setJustAccepted] = useState(false);
  const [asking, setAsking] = useState(false);
  const c = T[lang];

  useEffect(() => {
    document.title = `NEOSEUM · ${T[lang].head}`;
  }, [lang]);

  useEffect(() => {
    if (!token) {
      setState('not_found');
      return;
    }
    void call({ action: 'quote', token })
      .then(({ status, json }) => {
        if (status === 404) return setState('not_found');
        if (status !== 200 || !json?.quote) return setState('error');
        setQuote(json.quote);
        setLang(json.quote.locale === 'en' ? 'en' : 'nl');
        setState('ready');
      })
      .catch(() => setState('error'));
  }, [token]);

  async function act(action: 'accept' | 'decline') {
    setBusy(true);
    try {
      const { status, json } = await call({ action, token });
      if (status !== 200 || !json?.quote) {
        setState('error');
        return;
      }
      setQuote(json.quote);
      if (json.result === 'accepted') setJustAccepted(true);
    } catch {
      setState('error');
    } finally {
      setBusy(false);
      setAsking(false);
    }
  }

  let body: React.ReactNode;
  if (state === 'loading') body = <p className="muted">{c.loading}</p>;
  else if (state === 'not_found') body = <div className="card error-card"><p>{c.notFound}</p></div>;
  else if (state === 'error' || !quote) body = <div className="card error-card"><p>{c.error}</p></div>;
  else {
    const q = quote;
    const accepted = q.status === 'accepted' || q.status === 'invoiced' || q.status === 'paid';
    body = (
      <>
        {justAccepted ? (
          <div className="card pay-banner pay-banner-ok">
            <h2>{c.acceptedTitle}</h2>
            <p>{c.acceptedBody(q.email)}</p>
            <p>{c.arrive}</p>
          </div>
        ) : accepted ? (
          <div className="card pay-banner pay-banner-ok">
            <h2>{c.alreadyTitle}</h2>
            <p>{c.alreadyBody}</p>
          </div>
        ) : q.status === 'expired' ? (
          <div className="card error-card">
            <h2>{c.expiredTitle}</h2>
            <p>{c.expiredBody}</p>
            <div className="action-row">
              <a className="btn-primary" href={c.teamsUrl}>{c.newQuote}</a>
            </div>
          </div>
        ) : q.status === 'cancelled' && q.declined ? (
          <div className="card error-card">
            <h2>{c.declinedTitle}</h2>
            <p>{c.declinedBody}</p>
            <div className="action-row">
              <a className="btn-primary" href={c.teamsUrl}>{c.newQuote}</a>
            </div>
          </div>
        ) : q.status === 'cancelled' ? (
          <div className="card error-card">
            <h2>{c.cancelledTitle}</h2>
            <p>{c.cancelledBody}</p>
          </div>
        ) : null}

        <section className="card">
          <h2>{q.company}</h2>
          <dl className="kv">
            <dt>{c.quoteNo}</dt><dd>{q.quote_number}</dd>
            <dt>{c.location}</dt>
            <dd>
              {q.location.name}
              {q.location.address_line ? <><br /><span className="muted small">{q.location.address_line}, {q.location.postal_code} {q.location.city}</span></> : null}
            </dd>
            <dt>{c.date}</dt><dd>{day(q.start_at, lang)}</dd>
            <dt>{c.time}</dt><dd>{time(q.start_at)} – {time(q.end_at)}</dd>
            <dt>{c.people}</dt><dd>{q.group_size}</dd>
            <dt>{c.slots}</dt><dd>{c.slotsValue(q.slot_count, q.places_per_slot)}</dd>
            {q.parts.length > 1 ? (
              <>
                <dt>{c.schedule}</dt>
                <dd>
                  {q.parts.map((p, i) => (
                    <div key={p.slot_start}>{time(p.slot_start)} · {c.group(i + 1)} · {p.people}</div>
                  ))}
                </dd>
              </>
            ) : null}
            <dt>{c.exVat}</dt><dd>{money(q.total_ex_cents, lang)}</dd>
            <dt>{c.vat(q.vat_rate_pct)}</dt><dd>{money(q.vat_cents, lang)}</dd>
            <dt><strong>{c.incVat}</strong></dt><dd><strong>{money(q.total_inc_cents, lang)}</strong></dd>
          </dl>
        </section>

        {q.status === 'requested' ? (
          <section className="card">
            <p>{c.validUntil(`${day(q.expires_at, lang)} ${time(q.expires_at)}`)}</p>
            <p className="small muted">{c.terms}</p>
            <label className="small" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '0.75rem 0' }}>
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
              {c.agree}
            </label>
            <div className="action-row">
              <button className="btn-primary" disabled={!agree || busy || asking} onClick={() => void act('accept')}>
                {busy && !asking ? c.accepting : c.accept}
              </button>
              {!asking ? (
                <button className="btn-secondary" disabled={busy} onClick={() => setAsking(true)}>
                  {c.decline}
                </button>
              ) : null}
            </div>
            {asking ? (
              <div className="card error-card" role="alertdialog" aria-label={c.decline}>
                <p>{c.declineAsk}</p>
                <div className="action-row">
                  <button className="btn-danger" disabled={busy} onClick={() => void act('decline')}>
                    {busy ? c.declining : c.declineYes}
                  </button>
                  <button className="btn-secondary" disabled={busy} onClick={() => setAsking(false)}>
                    {c.declineNo}
                  </button>
                </div>
              </div>
            ) : null}
          </section>
        ) : null}
      </>
    );
  }

  return (
    <div className="manage-shell">
      <header className="manage-head">
        <h1>NEOSEUM</h1>
        <p className="muted">{c.head}</p>
      </header>
      <main className="manage-main">{body}</main>
      <footer className="manage-foot muted">
        {c.questions} <a href="mailto:bookings@neoseum.com">bookings@neoseum.com</a>
      </footer>
    </div>
  );
}
